/* ═══════════════════════════════════════════════════════════════
   Org Chart V2 — right-click node (person) context menu. Batch-aware
   ("(N selected)" suffix). NO "Hide from view" item (People Rebuild
   §3 placement rules — removed by grant, not ported: no hidden state
   exists in this build). Portal-free (positioned fixed like the old
   menu — dismissed by the shared mousedown-outside handler in
   OrgChartV2 via `data-orgchart-menu`).

   Main vs custom-BOARD matrix (migration 041, Josh grant 2026-08-17:
   "views become separate canvases/boards"): `isMain` swaps two things —
   (1) "Detach" is Main-hierarchy-only (it edits `reportsTo`, which is
   org-wide truth driven ONLY by Main; a custom board's lines are pure
   visual arrangement) — hidden entirely on a custom board; (2) the
   destructive "Remove from chart & unassign teams…" item (which also
   clears team_assignments) is Main-only — a custom board instead gets
   a plain, non-destructive "Remove from this view" (placement + this
   board's edges only, reversible via Cancel editing, so no separate
   confirm dialog).

   ⛔ THE LOADED GUN FIX (Josh incident 2026-08-17 — real data loss): the
   org-wide "Deactivate member…" item — the one that cascades to every
   team AND every board — is now Main-ONLY (`isMain` gate below). A
   custom board is a local arrangement of REAL people; nothing right-
   clicked there may ever touch org-wide person state except the
   explicit team items above. On Main the item is renamed from the old
   bare "Delete" (which read as "delete from this board" but actually
   deactivated the real employee firm-wide) to "Deactivate member…" —
   a control names exactly what it does — and its confirm copy (owned
   by PeoplePage's `confirmCopy`) already spells out the cascade.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import type { TeamMember, Team, TeamAssignment } from '../../../../../shared/types';
import { rem } from '../../../../lib/rem';

export interface NodeContextMenuProps {
  member: TeamMember;
  selectedIds: string[];
  members: TeamMember[];
  teams: Team[];
  teamAssignments: TeamAssignment[];
  x: number;
  y: number;
  /** True on Main, false on a custom board — see header note. */
  isMain: boolean;
  onDetach: (ids: string[]) => void;
  onToggleTeamRole: (memberId: string, teamId: string) => void;
  onTeamRemove: (memberId: string, teamId: string) => void;
  onAlign: (
    ids: string[],
    kind: 'left' | 'hcenter' | 'right' | 'top' | 'vcenter' | 'bottom'
  ) => void;
  onDistribute: (ids: string[], kind: 'horizontal' | 'vertical') => void;
  onRequestRemoveFromChart: (ids: string[], names: string) => void;
  /** Custom-board-only counterpart of `onRequestRemoveFromChart` — no
   *  confirm, no team-unassign, session-local (lands in
   *  removedMemberIds at Save). */
  onRemoveFromView: (ids: string[]) => void;
  onEditMember: (m: TeamMember) => void;
  /** Deactivates the real employee firm-wide (every team, every board).
   *  Main-ONLY — gated by `isMain` below; never rendered on a custom
   *  board. PeoplePage's confirm dialog is the actual gate/cascade
   *  copy, this menu just never offers the item elsewhere. */
  onDeleteMembers: (ids: string[]) => void;
  onClose: () => void;
}

export function NodeContextMenu({
  member,
  selectedIds,
  members,
  teams,
  teamAssignments,
  x,
  y,
  isMain,
  onDetach,
  onToggleTeamRole,
  onTeamRemove,
  onAlign,
  onDistribute,
  onRequestRemoveFromChart,
  onRemoveFromView,
  onEditMember,
  onDeleteMembers,
  onClose,
}: NodeContextMenuProps) {
  const isMulti = selectedIds.length > 1;
  const multiLabel = isMulti ? ` (${selectedIds.length} selected)` : '';
  const getMemberTeams = (mid: string): Team[] =>
    teamAssignments
      .filter((a) => a.teamMemberId === mid)
      .map((a) => teams.find((t) => t.id === a.teamId))
      .filter(Boolean) as Team[];

  const items: { label: string; action: () => void; danger?: boolean; separator?: boolean }[] = [];

  // "Detach" edits reportsTo — Main-hierarchy-only (see header note).
  if (isMain) {
    const detachable = selectedIds.filter((id) => {
      const mm = members.find((x2) => x2.id === id);
      return mm?.reportsTo && mm.reportsTo !== '__root__';
    });
    if (detachable.length > 0) {
      items.push({ label: `Detach${multiLabel}`, action: () => onDetach(detachable) });
    }
  }

  if (!isMulti) {
    const mTeams = getMemberTeams(member.id);
    const mTeamAssigns = teamAssignments.filter((a) => a.teamMemberId === member.id);
    for (const t of mTeams) {
      const assign = mTeamAssigns.find((a) => a.teamId === t.id);
      const isLeader = assign?.teamRole === 'leader';
      items.push({
        label: `${t.name}: ${isLeader ? 'Demote to Member' : 'Promote to Leader'}`,
        action: () => onToggleTeamRole(member.id, t.id),
      });
    }
  }

  const allTeamIds = new Set(
    selectedIds.flatMap((id) =>
      teamAssignments.filter((a) => a.teamMemberId === id).map((a) => a.teamId)
    )
  );
  for (const tid of allTeamIds) {
    const t = teams.find((x2) => x2.id === tid);
    if (t) {
      items.push({
        label: `Remove from ${t.name}${multiLabel}`,
        action: () => {
          for (const id of selectedIds) onTeamRemove(id, tid);
        },
      });
    }
  }

  if (isMulti) {
    if (items.length > 0) items.push({ label: '', action: () => {}, separator: true });
    items.push({ label: 'Align left', action: () => onAlign(selectedIds, 'left') });
    items.push({ label: 'Align horizontal center', action: () => onAlign(selectedIds, 'hcenter') });
    items.push({ label: 'Align right', action: () => onAlign(selectedIds, 'right') });
    items.push({ label: 'Align top', action: () => onAlign(selectedIds, 'top') });
    items.push({ label: 'Align vertical center', action: () => onAlign(selectedIds, 'vcenter') });
    items.push({ label: 'Align bottom', action: () => onAlign(selectedIds, 'bottom') });
    if (selectedIds.length >= 3) {
      items.push({
        label: 'Distribute horizontally',
        action: () => onDistribute(selectedIds, 'horizontal'),
      });
      items.push({
        label: 'Distribute vertically',
        action: () => onDistribute(selectedIds, 'vertical'),
      });
    }
  }

  if (items.length > 0) items.push({ label: '', action: () => {}, separator: true });
  if (isMain) {
    const removable = selectedIds.filter((id) => {
      const mm = members.find((x2) => x2.id === id);
      return mm?.reportsTo || getMemberTeams(id).length > 0;
    });
    if (removable.length > 0) {
      items.push({
        label: `Remove from chart & unassign teams${multiLabel}...`,
        danger: true,
        action: () => {
          const names = removable
            .map((id) => members.find((mm) => mm.id === id)?.name)
            .filter(Boolean)
            .join(', ');
          onRequestRemoveFromChart(removable, names);
        },
      });
    }
  } else {
    // Custom board: everyone right-clickable is, by construction,
    // placed on this board — always removable, non-destructive (see
    // header note).
    items.push({
      label: `Remove from this view${multiLabel}`,
      action: () => onRemoveFromView(selectedIds),
    });
  }
  if (!isMulti) {
    items.push({ label: 'Edit member...', action: () => onEditMember(member) });
  }
  // Main-ONLY (see header note) — a custom board must never expose an
  // action that deactivates the real employee. On Main this cascades
  // (every team + every board), so the label says so; PeoplePage's own
  // confirm dialog spells out the full cascade before anything happens.
  if (isMain) {
    items.push({
      label: `Archive member…${multiLabel}`,
      action: () => onDeleteMembers(selectedIds),
      danger: true,
    });
  }

  return (
    <div
      data-orgchart-menu
      role="menu"
      style={{
        position: 'fixed',
        left: x,
        top: y,
        background: 'var(--sf)',
        border: '1px solid var(--bd)',
        borderRadius: rem(8),
        boxShadow: '0 8px 24px rgba(0,0,0,.18)',
        padding: 'var(--space-1) 0',
        minWidth: rem(180),
        zIndex: 1000,
      }}
    >
      {items.map((item, i) =>
        item.separator ? (
          <div key={i} style={{ height: rem(1), background: 'var(--bd)', margin: rem(3) + ' 0' }} />
        ) : (
          <button
            key={i}
            onClick={() => {
              onClose();
              item.action();
            }}
            style={{
              display: 'block',
              width: '100%',
              textAlign: 'left',
              padding: rem(7) + ' ' + rem(14),
              fontSize: 'var(--font-size-base)',
              fontWeight: 500,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: item.danger ? 'var(--dg)' : 'var(--tx)',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--sa)')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
          >
            {item.label}
          </button>
        )
      )}
    </div>
  );
}
